import { useEffect, useCallback, useState } from 'react'
import useFormikHelper from './useFormikHelper'
import { useLabWaterParticipantId } from '@src/utils/useLabWaterParticipantId'
import { useCurrentUser } from '@src/utils/useCurrentUser'
import {
  TubesFormValues,
  useProductFromQueryParam,
} from './ProductsConfiguration'
import CircleLoader from '@src/components/CircleLoader'
import { useProductFromQueryParam as useProductFromQueryParamSemiConfig } from '@src/routes/SemiConfigurators/products/SemiConfiguratorRedesign/ProductsConfiguration'

const FormikLocalStorage = ({
  semiConfigProduct = false,
  children,
}: {
  semiConfigProduct?: boolean
  children: React.ReactNode
}) => {
  const expirationDays = 365
  const product = semiConfigProduct
    ? useProductFromQueryParamSemiConfig()
    : useProductFromQueryParam()
  const {
    allValues,
    setValues,
    setFieldTouched,
    setFieldError,
    activeOligoIndex,
    fieldsErrorKeyMap,
  } = useFormikHelper()
  const { userIsLoggedIn } = useCurrentUser()
  const participantId = useLabWaterParticipantId()
  const key = `${product}-${userIsLoggedIn ? participantId : 'guest'}`
  const [initialLoadCompleted, setInitialLoadCompleted] =
    useState<boolean>(false)

  const loadFromStorage = useCallback(() => {
    let savedData: TubesFormValues | null = null
    if (!userIsLoggedIn) {
      const data = localStorage.getItem(key)
      if (data) {
        const { data: formData, expiration } = JSON.parse(data)
        if (new Date().getTime() < expiration) {
          savedData = formData
        } else {
          localStorage.removeItem(key)
        }
      }
    } else {
      const data = localStorage.getItem(key)
      const guestKey = `${product}-guest`
      const guestData = localStorage.getItem(guestKey)
      if (data) {
        const { data: loggedInFormData, expiration } = JSON.parse(data)
        if (guestData) {
          const { data: guestFormData } = JSON.parse(guestData)
          if (semiConfigProduct) {
            savedData = { ...loggedInFormData, ...guestFormData }
          } else {
            const validGuestOligos = guestFormData.oligoList.filter(
              (oligo) =>
                oligo.name.trim() !== '' || oligo.sequence.trim() !== ''
            )
            savedData = {
              ...loggedInFormData,
              oligoList: [
                ...loggedInFormData.oligoList.filter(
                  (oligo) =>
                    oligo.name.trim() !== '' || oligo.sequence.trim() !== ''
                ),
                ...validGuestOligos,
              ],
            }
          }
          const { expiration: guestExpiration } = JSON.parse(guestData)
          localStorage.setItem(
            key,
            JSON.stringify({
              data: savedData,
              expiration: guestExpiration,
            })
          )

          localStorage.removeItem(guestKey)
        } else {
          if (new Date().getTime() < expiration) {
            savedData = loggedInFormData
          } else {
            localStorage.removeItem(key)
          }
        }
      } else if (guestData) {
        // Only guest data exists
        const { data: guestFormData, expiration } = JSON.parse(guestData)
        if (new Date().getTime() < expiration) {
          if (semiConfigProduct) {
            savedData = { ...guestFormData }
          } else {
            const validGuestOligos = guestFormData.oligoList.filter(
              (oligo) =>
                oligo.name.trim() !== '' || oligo.sequence.trim() !== ''
            )
            savedData = {
              ...guestFormData,
              oligoList: validGuestOligos,
            }
          }

          localStorage.setItem(
            key,
            JSON.stringify({ data: savedData, expiration })
          )
          localStorage.removeItem(guestKey)
        } else {
          localStorage.removeItem(key)
        }
      }
    }

    return savedData
  }, [key, userIsLoggedIn, participantId, product])

  const saveToStorage = useCallback(
    (dataToSave) => {
      const expirationDate = new Date()
      expirationDate.setDate(expirationDate.getDate() + expirationDays)
      const dataWithExpiration = {
        data: dataToSave,
        expiration: expirationDate.getTime(),
      }
      localStorage.setItem(key, JSON.stringify(dataWithExpiration))
    },
    [key, expirationDays]
  )

  useEffect(() => {
    const storedData = loadFromStorage()
    if (storedData) {
      setValues(storedData)
      // when loading from local storage, touch only inputs with an error and value. this prevents sequence error on page refresh or initial load
      if (
        !semiConfigProduct &&
        storedData.oligoList[activeOligoIndex]?.listErrors?.length > 0
      ) {
        const selectedOligo = storedData.oligoList[activeOligoIndex].listErrors
        selectedOligo.forEach((error) => {
          const key = fieldsErrorKeyMap[error?.key]
            ? fieldsErrorKeyMap[error?.key]
            : error?.key
          setFieldTouched(key, true, false)
          setFieldError(key, error?.value)
        })
      }
    }
    setInitialLoadCompleted(true)
  }, [loadFromStorage, setValues])

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      saveToStorage(allValues)
    }, 800)

    return () => clearTimeout(timeoutId)
  }, [allValues, saveToStorage])

  return <>{initialLoadCompleted ? children : <CircleLoader />}</>
}

export default FormikLocalStorage
